import type { ReactElement, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { OrgAnalytics } from '@contracts/analytics';
import { useOrg, useOrgAnalytics } from '../api/queries';
import { ApiError } from '../api/client';
import { Broken, Card, Empty, Pill, SkeletonRows } from '../ui';
import { ago, duration } from '../lib/time';
import { useTab } from '../lib/url-tab';

/**
 * PRD 34 — the workspace counted and timed, for an administrator.
 *
 * Three questions, in the order an administrator asks them: is the team using
 * it, what did it produce, how did the notetaker do — then the same day by
 * day, per series, and the members. Every number is the server's; the page
 * only formats. Nothing here is a sentence anyone said, **nothing here is
 * money** (what a meeting costs is the operator's to know), and a member who
 * finds the address is told whose page it is.
 *
 * The per-person table exists only when the workspace turned it on
 * (Recording defaults → Privacy): members only, counts only — no talk time, no
 * completion rate, nothing that ranks one named colleague against another
 * (ADR-026 §8).
 */
type RangeKey = '7d' | '30d' | 'month' | '90d';
const RANGES: { key: RangeKey; label: string }[] = [
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'month', label: 'This month' },
  { key: '90d', label: 'Last 90 days' },
];
const DAY = 24 * 60 * 60 * 1000;

function rangeOf(key: RangeKey, now: Date): { from: string; to: string; words: string } {
  const to = now;
  if (key === 'month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      from: from.toISOString(),
      to: to.toISOString(),
      words: `${from.toLocaleDateString(undefined, { month: 'long' })} so far`,
    };
  }
  const days = key === '7d' ? 7 : key === '30d' ? 30 : 90;
  return {
    from: new Date(now.getTime() - days * DAY).toISOString(),
    to: to.toISOString(),
    words: `the last ${days} days`,
  };
}

export function Analytics(): ReactElement {
  const org = useOrg();
  const [now] = useState(() => new Date());
  const [key, setKey] = useTab<RangeKey>(
    'range',
    RANGES.map((r) => r.key),
    '30d',
  );
  const range = useMemo(() => rangeOf(key, now), [key, now]);
  const q = useOrgAnalytics(range.from, range.to);
  const forAdmins = q.isError && q.error instanceof ApiError && q.error.status === 404;

  // ⚠ A page of its own, opened from the account menu — not a settings page:
  // nothing here is set, it is read. Vladimir, 2026-09-24: "Move analytics
  // from settings to the users profile dropdown".
  return (
    <div className="page stack-lg">
      <div className="page-head" style={{ marginBottom: 0 }}>
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="t-title">Analytics</h1>
          <p className="mono">{`${range.words} · counts and durations — never a word anyone said`}</p>
        </div>
        <div className="segmented" role="tablist" aria-label="Range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              role="tab"
              aria-selected={r.key === key}
              className={r.key === key ? 'on' : ''}
              onClick={() => setKey(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {q.isPending ? (
        <SkeletonRows rows={6} />
      ) : forAdmins ? (
        <Card flush>
          <Empty lead="Analytics are for administrators.">
            {org.data
              ? `An administrator of ${org.data.name} can read them, or make you one under People and roles.`
              : 'An administrator of your workspace can read them, or make you one.'}
          </Empty>
        </Card>
      ) : q.isError ? (
        <Card flush>
          <Broken what="Analytics" retry={() => void q.refetch()} />
        </Card>
      ) : (
        <Report a={q.data} range={range} />
      )}
    </div>
  );
}

function Report({
  a,
  range,
}: {
  a: OrgAnalytics;
  range: { from: string; to: string };
}): ReactElement {
  const days = useMemo(() => fillDays(range.from, range.to, a.days), [range, a.days]);
  const c = a.produced.commitments;
  const nothing = a.use.meetings === 0;
  return (
    <>
      <Card title="Use" aside={<span className="t-secondary">{a.use.members} members</span>}>
        <div className="figures wrap">
          <Figure n={a.use.meetings} k="meetings" />
          <Figure n={duration(a.use.seconds)} k="in calls" />
          <Figure n={a.use.meetings ? duration(a.use.averageSeconds) : '—'} k="average" />
          <Figure
            n={`${a.use.activeMembers7} of ${a.use.members}`}
            k="active this week"
            amber={a.use.members > 0 && a.use.activeMembers7 < a.use.members}
          />
          <Figure n={a.use.activeMembers30} k="active in 30 days" />
          <Figure n={a.use.guestsMet} k="guests met" />
          <Figure
            n={a.use.bots.secondsToNote === null ? '—' : duration(a.use.bots.secondsToNote)}
            k="hang-up to note, median"
          />
        </div>
        {nothing ? (
          <p className="t-secondary" style={{ marginTop: 12 }}>
            No meeting in this range. Pick a longer one, or wait for the next call.
          </p>
        ) : null}
      </Card>

      <Card title="Day by day">
        <div className="stack-lg">
          <Bars
            label="Meetings"
            days={days}
            value={(d) => d.meetings}
            format={(n) => `${n} ${n === 1 ? 'meeting' : 'meetings'}`}
          />
          <Bars
            label="Commitments opened · closed"
            days={days}
            value={(d) => d.opened}
            second={(d) => d.closed}
            format={(n) => `${n}`}
          />
        </div>
      </Card>

      <Card title="Produced">
        <div className="figures wrap">
          <Figure n={c.opened} k="commitments opened" />
          <Figure n={c.closed} k="closed" />
          <Figure n={c.open} k="open now" />
          <Figure n={c.openOverWeek} k="open over a week" amber={c.openOverWeek > 0} />
          <Figure n={c.ownerGuessed} k="owner guessed" amber={c.ownerGuessed > 0} />
          <Figure
            n={c.medianDaysToClose === null ? '—' : `${c.medianDaysToClose} d`}
            k="median to close"
          />
          <Figure n={a.produced.decisions.made} k="decisions" />
          <Figure n={a.produced.decisions.superseded} k="superseded" />
          <Figure n={a.produced.notesWritten} k="notes written" />
        </div>
        <dl className="breakdown">
          <Row k="By evidence">{pairs(c.byGrade)}</Row>
          <Row k="How they ended">{pairs(c.byEnd)}</Row>
          <Row k="Who closed them">
            {c.byPerson} by a person · {c.byListening} by what was said in a later meeting
          </Row>
          <Row k="Words transcribed">{pairs(a.produced.wordsByLanguage, true)}</Row>
        </dl>
      </Card>

      <Card title="The notetaker">
        <div className="figures wrap">
          <Figure n={a.use.reach.events} k="calendar events" />
          <Figure n={a.use.reach.withLink} k="with a call link" />
          <Figure n={a.use.reach.recorded} k="recorded" />
          <Figure n={a.use.reach.noLink} k="no link" />
          <Figure n={a.use.reach.skipped} k="skipped on purpose" />
        </div>
        <div className="figures wrap" style={{ marginTop: 16 }}>
          <Figure n={a.use.bots.sent} k="bots sent" />
          <Figure n={a.use.bots.joined} k="joined" />
          <Figure
            n={a.use.bots.waitedAtTheDoor}
            k="waited at the door"
            amber={a.use.bots.waitedAtTheDoor > 0}
          />
          <Figure n={a.use.bots.failed} k="failed" amber={a.use.bots.failed > 0} />
        </div>
      </Card>

      <Card title="Volume">
        <div className="figures wrap">
          <Figure n={`${a.volume.recordedHours.toFixed(1)} h`} k="recorded and transcribed" />
          {a.volume.aiCalls.map((k) => (
            <Figure key={k.kind} n={k.calls} k={kindWords(k.kind)} />
          ))}
          <Figure n={a.volume.mcpCalls} k="calls from connected apps" />
          <Figure n={gb(a.volume.videoBytesKept)} k="video kept" />
        </div>
      </Card>

      <Card title="Per series">
        {a.series.length === 0 ? (
          <p className="t-secondary">No recurring meeting in this range.</p>
        ) : (
          <div className="scroll-x">
            <table className="table">
              <thead>
                <tr>
                  <th>Series</th>
                  <th className="num">Instances</th>
                  <th className="num">Time</th>
                  <th className="num">Open</th>
                  <th className="num">Closed by listening</th>
                </tr>
              </thead>
              <tbody>
                {a.series.map((s) => (
                  <tr key={s.seriesKey}>
                    <td>
                      <Link to={`/series/${encodeURIComponent(s.seriesKey)}`}>{s.title}</Link>
                    </td>
                    <td className="num">{s.instances}</td>
                    <td className="num">{duration(s.seconds)}</td>
                    <td className="num">{s.open}</td>
                    <td className="num">{s.closedByListening}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Per person"
        aside={
          <span className="t-secondary">
            {a.peopleEnabled ? 'members only · counts, never content' : 'off'}
          </span>
        }
      >
        {!a.peopleEnabled ? (
          <p className="t-secondary">
            This workspace does not count its people by name. An administrator can turn the table on
            under <Link to="/settings/organisation/defaults">Recording defaults → Privacy</Link> —
            it shows each member’s meetings, time in calls and open and done commitments, and
            members are told it is on.
          </p>
        ) : a.people.length === 0 ? (
          <p className="t-secondary">No member was in a call in this range.</p>
        ) : (
          <div className="scroll-x">
            <table className="table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th className="num">Meetings</th>
                  <th className="num">Time</th>
                  <th className="num">Open</th>
                  <th className="num">Done</th>
                </tr>
              </thead>
              <tbody>
                {a.people.map((p) => (
                  <tr key={p.email}>
                    <td>
                      <Link to={`/people/${encodeURIComponent(p.email)}`}>{p.name ?? p.email}</Link>
                    </td>
                    <td className="num">{p.meetings}</td>
                    <td className="num">{duration(p.seconds)}</td>
                    <td className="num">{p.owes}</td>
                    <td className="num">{p.done}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Members"
        aside={<span className="t-secondary">inactive = nothing in two weeks</span>}
      >
        <div className="scroll-x">
          <table className="table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Last active</th>
              </tr>
            </thead>
            <tbody>
              {a.members.map((m) => (
                <tr key={m.email}>
                  <td>{m.name ?? m.email}</td>
                  <td className="mono">{m.role.toLowerCase()}</td>
                  <td>
                    {m.lastActiveAt ? ago(m.lastActiveAt) : 'never'}{' '}
                    {m.inactive ? <Pill tone="amber">inactive</Pill> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// --- pieces

function Figure({
  n,
  k,
  amber = false,
}: {
  n: ReactNode;
  k: string;
  amber?: boolean;
}): ReactElement {
  return (
    <div className="figure">
      <span className={`n${amber ? ' amber' : ''}`}>{n}</span>
      <span className="k">{k}</span>
    </div>
  );
}

function Row({ k, children }: { k: string; children: ReactNode }): ReactElement {
  return (
    <div className="breakdown-row">
      <dt className="t-secondary">{k}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function pairs(record: Record<string, number>, thousands = false): string {
  const entries = Object.entries(record).sort((x, y) => y[1] - x[1]);
  if (entries.length === 0) return '—';
  return entries.map(([k, v]) => `${k} ${thousands ? v.toLocaleString() : v}`).join(' · ');
}

type Day = OrgAnalytics['days'][number];

/** Every day of the range, in order, zero where nothing happened. */
function fillDays(from: string, to: string, days: Day[]): Day[] {
  const byDay = new Map(days.map((d) => [d.day, d]));
  const out: Day[] = [];
  for (let t = new Date(from.slice(0, 10)).getTime(); ; t += DAY) {
    const key = new Date(t).toISOString().slice(0, 10);
    if (key > to.slice(0, 10)) break;
    out.push(byDay.get(key) ?? { day: key, meetings: 0, seconds: 0, opened: 0, closed: 0 });
    if (out.length > 400) break;
  }
  return out;
}

/**
 * One scale, bars for every day of the range, the peak named. A second series
 * draws beside the first, the same scale. Plain SVG; the page is not a chart
 * library's to load.
 */
function Bars({
  label,
  days,
  value,
  second,
  format,
  tone = 'ink',
}: {
  label: string;
  days: Day[];
  value: (d: Day) => number;
  second?: (d: Day) => number;
  format: (n: number) => string;
  tone?: 'ink' | 'amber';
}): ReactElement {
  const W = 640;
  const H = 96;
  const PAD = 4;
  const max = Math.max(1, ...days.map((d) => Math.max(value(d), second ? second(d) : 0)));
  const peak = days.reduce<Day | null>(
    (best, d) => (best === null || value(d) > value(best) ? d : best),
    null,
  );
  const slot = (W - PAD * 2) / Math.max(1, days.length);
  const bw = second ? Math.max(1, slot * 0.4) : Math.max(1, slot * 0.7);
  const y = (v: number) => H - (v / max) * (H - 8);
  const total = days.reduce((s, d) => s + value(d), 0);
  const label2 = second ? days.reduce((s, d) => s + second(d), 0) : null;
  return (
    <div className="chart">
      <div className="chart-head">
        <span className="t-secondary">{label}</span>
        <span className="mono">
          {second ? `${format(total)} · ${format(label2 ?? 0)}` : format(total)}
          {peak && value(peak) > 0 ? ` · peak ${format(value(peak))} on ${dayWords(peak.day)}` : ''}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H + 18}`} role="img" aria-label={`${label}, day by day`}>
        <line x1={PAD} x2={W - PAD} y1={H} y2={H} className="axis" />
        {days.map((d, i) => {
          const x = PAD + i * slot + (slot - (second ? bw * 2 + 2 : bw)) / 2;
          return (
            <g key={d.day}>
              <rect
                x={x}
                y={y(value(d))}
                width={bw}
                height={H - y(value(d))}
                className={`bar ${tone}`}
              >
                <title>{`${dayWords(d.day)}: ${format(value(d))}`}</title>
              </rect>
              {second ? (
                <rect
                  x={x + bw + 2}
                  y={y(second(d))}
                  width={bw}
                  height={H - y(second(d))}
                  className="bar second"
                >
                  <title>{`${dayWords(d.day)}: ${format(second(d))}`}</title>
                </rect>
              ) : null}
            </g>
          );
        })}
        {days.length > 0 ? (
          <>
            <text x={PAD} y={H + 14} className="tick">
              {dayWords(days[0]!.day)}
            </text>
            <text x={W - PAD} y={H + 14} className="tick" textAnchor="end">
              {dayWords(days[days.length - 1]!.day)}
            </text>
          </>
        ) : null}
      </svg>
    </div>
  );
}

function dayWords(day: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function gb(bytes: number): string {
  if (bytes === 0) return 'none';
  if (bytes < 1024 ** 3) return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function kindWords(kind: string): string {
  switch (kind) {
    case 'EXTRACT':
      return 'commitments and decisions read out of transcripts';
    case 'ENHANCE':
      return 'notes written';
    case 'ASK':
      return 'questions answered';
    default:
      return kind.toLowerCase().replace(/_/g, ' ');
  }
}
